/**
 * ARRANGING THE DESK (2026-10-31, Andre, from the design ideation of the same
 * day — "The Arranged Worktable"): an arrangement is a COMMAND, never a mode.
 * It reads where the papers lie, works out once where they would lie tidily,
 * moves them there, and hands the desk straight back: every paper is as free
 * to drag afterwards as it was before, and nothing that arrives or changes
 * later repeats the command. The desk owns the learner's placement; a command
 * only offers a better starting point.
 *
 * This module is the arithmetic alone — pure, deterministic, blind to React
 * and to the DOM — so it can be proved on empty, sparse and crowded desks
 * without a browser (desk-arrange.test.ts). The screen measures the papers,
 * calls it, and commits the result through its own placement state
 * (home.tsx), which is also what keeps an arrangement well away from the bin:
 * an arrangement writes offsets, it never drops anything anywhere.
 *
 * "TIDY" is the first and plainest arrangement: spread the papers into
 * columns with their titles and doors showing, in the order they already lie
 * (rows top to bottom, left to right within a row), so a learner's rough
 * grouping survives the squaring-up. When the desk is too short for the whole
 * grid the rows close up into a cascade, like the stack the desk deals, but
 * never tighter than `minStep`, so each paper keeps a strip a hand can take.
 * The later arrangements the design names (by status, AJ's order, by skill,
 * my focus) will be other orderings fed to the same grid.
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** One paper as the arithmetic sees it: its key, its size, where it rests untouched, and the offset it has been dragged to. */
export interface ArrangeablePaper {
  readonly key: string;
  /** Its slot in the dealt stack: arrival order, the tie-breaker when two papers lie on the same spot. */
  readonly slot: number;
  /** Where the paper rests when untouched (its slot's own place), in px within the surface. */
  readonly rest: Point;
  /** Where the learner left it, as the offset from `rest`, in px. */
  readonly offset: Point;
  readonly width: number;
  readonly height: number;
}

export interface ArrangeSurface {
  readonly width: number;
  readonly height: number;
}

export interface TidyOptions {
  /** The gutter between papers, in px. */
  readonly gap: number;
  /** The tightest a cascade may close, in px: a paper never loses more than this of its top strip. */
  readonly minStep: number;
  /** Where the grid begins, in px: the stack's own inset, so a tidied desk lines up with a dealt one. */
  readonly origin: Point;
}

export interface Arrangement {
  /** Each paper's new offset from its resting place, by key — exactly what the desk remembers. */
  readonly offsets: Readonly<Record<string, Point>>;
  /** The papers in the order they were laid, first to last; raised in this order a cascade shows each paper's own top. */
  readonly order: readonly string[];
}

const left = (paper: ArrangeablePaper): number => paper.rest.x + paper.offset.x;
const top = (paper: ArrangeablePaper): number => paper.rest.y + paper.offset.y;
const byArrival = (a: ArrangeablePaper, b: ArrangeablePaper): number => a.slot - b.slot || a.key.localeCompare(b.key);

/**
 * Papers whose leading edges lie within `tolerance` of the group's first are
 * one group: the rows of a desk read by their tops, the piles within a row read
 * by their lefts. Sorted on the edge first so a group is a run, with arrival
 * order breaking exact ties, so the grouping never depends on the input's order.
 */
function groupBy(papers: readonly ArrangeablePaper[], edge: (paper: ArrangeablePaper) => number, tolerance: (paper: ArrangeablePaper) => number): readonly (readonly ArrangeablePaper[])[] {
  const sorted = [...papers].sort((a, b) => edge(a) - edge(b) || byArrival(a, b));
  const groups: ArrangeablePaper[][] = [];
  let current: ArrangeablePaper[] | null = null;
  let start = 0;
  let within = 0;
  for (const paper of sorted) {
    if (current === null || edge(paper) - start > within) {
      current = [paper];
      groups.push(current);
      start = edge(paper);
      within = Math.max(1, tolerance(paper));
    } else {
      current.push(paper);
    }
  }
  return groups;
}

/**
 * The papers as they lie, read like a page. Rows are papers whose tops lie
 * within `rowTolerance` of the row's first (and never more than half a paper's
 * height); within a row, piles are papers whose lefts lie within half a
 * paper's width of the pile's first, and a pile reads top to bottom. So the
 * dealt stack — one pile, a small alternating lean — reads in arrival order;
 * two piles side by side read as two piles; a spread grid reads left to right,
 * row by row.
 */
export function readingOrder(papers: readonly ArrangeablePaper[], rowTolerance: number = Number.POSITIVE_INFINITY): readonly ArrangeablePaper[] {
  const rows = groupBy(papers, top, (paper) => Math.min(paper.height / 2, rowTolerance));
  return rows.flatMap((row) => groupBy(row, left, (paper) => paper.width / 2).flatMap((pile) => [...pile].sort((a, b) => top(a) - top(b) || byArrival(a, b))));
}

/** Two whole-pixel roundings: a tidied row's tops can differ by this much and still be one row. */
const ROUNDING_SLACK = 2;

/**
 * TIDY. Columns as many as the surface's width holds at the papers' width and
 * the gutter; rows as many as the papers need. The rows stand a gutter apart
 * when the surface is tall enough for all of them; otherwise they close up to
 * an even cascade whose every row still ends inside the surface — the step is
 * set by the row that would run past the foot first, since rows differ in
 * height (an exercise paper is twice an email card) — and no row sits lower
 * than it would have stood naturally. The cascade closes no tighter than
 * `minStep`; then, and only then, the last rows run past the foot — a desk
 * too small for its papers cannot be made to hold them, and it is better to
 * say so than to hide one. Every paper is placed exactly once; an empty desk
 * is an empty arrangement.
 */
export function tidy(surface: ArrangeSurface, papers: readonly ArrangeablePaper[], options: TidyOptions): Arrangement {
  if (papers.length === 0) return { offsets: {}, order: [] };
  // The stack's own step is the desk's unit of "a different row": a cascade this
  // command lays is never tighter, so tidying a tidied desk reads it back row by
  // row and changes nothing — while a hand-laid row with a little jitter in it still reads as one.
  const ordered = readingOrder(papers, Math.max(1, options.minStep - ROUNDING_SLACK));
  const paperWidth = Math.max(...ordered.map((paper) => paper.width));
  const usableWidth = Math.max(0, surface.width - options.origin.x);
  const usableHeight = Math.max(0, surface.height - options.origin.y);
  const columns = Math.max(1, Math.floor((usableWidth + options.gap) / (paperWidth + options.gap)));
  const rows = Math.ceil(ordered.length / columns);
  const rowHeights = Array.from({ length: rows }, (_, row) => Math.max(...ordered.slice(row * columns, (row + 1) * columns).map((paper) => paper.height)));
  const natural = rowHeights.reduce((sum, height) => sum + height, 0) + (rows - 1) * options.gap;

  const rowTops: number[] = [];
  if (rows === 1 || natural <= usableHeight) {
    let y = 0;
    for (const height of rowHeights) {
      rowTops.push(y);
      y += height + options.gap;
    }
  } else {
    // The widest step at which every row's foot stays on the desk: row `i` ends at `i * step + height_i`.
    const fits = Math.min(...rowHeights.slice(1).map((height, i) => (usableHeight - height) / (i + 1)));
    const step = Math.max(options.minStep, fits);
    let previousBottom = 0;
    for (let row = 0; row < rows; row += 1) {
      const y = row === 0 ? 0 : Math.min(row * step, previousBottom + options.gap);
      rowTops.push(y);
      previousBottom = y + (rowHeights[row] ?? 0);
    }
  }

  const offsets: Record<string, Point> = {};
  const order: string[] = [];
  ordered.forEach((paper, at) => {
    const row = Math.floor(at / columns);
    const column = at % columns;
    let x = options.origin.x + column * (paperWidth + options.gap);
    // A paper wider than the room it has (a phone's desk) keeps its left edge on the desk rather than its right.
    if (x + paper.width > surface.width) x = Math.max(0, surface.width - paper.width);
    const y = options.origin.y + (rowTops[row] ?? 0);
    offsets[paper.key] = { x: Math.round(x - paper.rest.x), y: Math.round(y - paper.rest.y) };
    order.push(paper.key);
  });
  return { offsets, order };
}

/**
 * STACKING BY KIND (2026-10-31, Andre's second pass on the arrangement: "a
 * version of tidy that stacks the cards by type … keep the top-priority cards
 * visible, and solve the overcrowding when there are more than six"). The
 * papers of one kind lie in one pile; the piles stand side by side, left to
 * right in the order `kinds` names them (the coach's cases first). In a pile
 * the paper of first standing lies whole, and every other paper shows a strip
 * of its own top above it, the nearer the strip the sooner it comes — the
 * dealt stack's own cascade, turned so the paper to open first is the one
 * that shows. On a desk wide enough the whole papers of every pile stand on
 * one line, so a desk of ten reads as a row of what to open next with the
 * rest filed behind it; on a narrower desk the piles fill columns, each new
 * pile going beneath the shortest column, so every pile still shows whole.
 */
export interface StackablePaper extends ArrangeablePaper {
  /** The pile it lies in: papers of one kind lie together. */
  readonly kind: string;
  /** Its standing within its kind, first is 0: the first lies whole, the rest file behind it in this order. */
  readonly standing: number;
}

export interface StackOptions {
  /** The gutter between piles, across and down, in px. */
  readonly gap: number;
  /** The strip each filed paper shows above the one in front of it, in px: the dealt stack's own step. */
  readonly step: number;
  /** The tightest a pile may close when the desk is too short, and the least a pile keeps showing when a column must close up, in px. */
  readonly minStep: number;
  /** Where the piles begin, in px: the stack's own inset, so a stacked desk lines up with a dealt one. */
  readonly origin: Point;
  /** The kinds left to right. A kind not named here follows the named ones, in the order its first paper arrived. */
  readonly kinds: readonly string[];
}

const byStanding = (a: StackablePaper, b: StackablePaper): number => a.standing - b.standing || byArrival(a, b);

/** The piles, left to right: the named kinds in their order, then any other kind by its first paper's arrival; each pile in order of standing. */
function piles(papers: readonly StackablePaper[], kinds: readonly string[]): readonly (readonly StackablePaper[])[] {
  const byKind = new Map<string, StackablePaper[]>();
  for (const paper of [...papers].sort(byArrival)) {
    const pile = byKind.get(paper.kind);
    if (pile === undefined) byKind.set(paper.kind, [paper]);
    else pile.push(paper);
  }
  const named = kinds.flatMap((kind) => { const pile = byKind.get(kind); return pile === undefined ? [] : [pile]; });
  const unnamed = [...byKind.entries()].filter(([kind]) => !kinds.includes(kind)).map(([, pile]) => pile);
  return [...named, ...unnamed].map((pile) => [...pile].sort(byStanding));
}

/** A pile standing somewhere on the desk: which column, where its top is, and how far down it reaches. */
interface StandingPile {
  readonly pile: readonly StackablePaper[];
  readonly column: number;
  readonly top: number;
  readonly height: number;
}

/**
 * STACK BY KIND. One pile a kind, in columns a paper wide and a gutter apart
 * from the origin, as many as the desk's width holds. When every pile has a
 * column of its own they stand side by side and the whole paper of each pile
 * lies on one line, the deepest pile's strips rising above it; when there are
 * more piles than columns, each pile in turn goes to the shortest column, a
 * gutter below what stands there, so a narrow desk reads as columns of whole
 * papers rather than a row of hidden ones. Within a pile the first paper lies
 * whole with the rest filed above it a `step` apart. When the desk is too
 * short for the deepest pile, the strips close evenly, never tighter than
 * `minStep`; when a column is still too tall, its piles close up evenly as
 * well, each keeping its strips and a strip of its whole paper clear of the
 * pile below, and then, and only then, a pile runs past the foot — a desk too small for its papers cannot
 * be made to hold them, and it is better to say so than to hide one. Every
 * paper is placed exactly once; an empty desk is an empty arrangement. The
 * order of standing runs column by column from the last to the first, down
 * each column, and within a pile from the deepest paper to the whole one, so
 * raised in this order each paper shows its own strip, the first pile lies
 * over its neighbours, and a pile closed up over the one above it shows its
 * top.
 */
export function stackByKind(surface: ArrangeSurface, papers: readonly StackablePaper[], options: StackOptions): Arrangement {
  if (papers.length === 0) return { offsets: {}, order: [] };
  const stacks = piles(papers, options.kinds);
  const usableWidth = Math.max(0, surface.width - options.origin.x);
  const usableHeight = Math.max(0, surface.height - options.origin.y);

  // The strips: the step the desk deals unless the deepest pile would then run
  // past the foot, in which case the widest step at which every filed paper's
  // foot stays on the desk — paper `r` deep in a pile whose whole paper stands
  // `depth` strips down ends at `(depth - r) * step + height`.
  const depth = Math.max(...stacks.map((pile) => pile.length - 1));
  const limits = stacks.flatMap((pile) => pile.flatMap((paper, r) => (depth - r > 0 ? [(usableHeight - paper.height) / (depth - r)] : [])));
  const fits = limits.length === 0 ? options.step : Math.min(...limits);
  const step = depth === 0 ? 0 : Math.min(options.step, Math.max(options.minStep, fits));
  /** How far down a pile reaches from its top: its own strips, then the tallest paper measured from where it lies. */
  const reach = (pile: readonly StackablePaper[]): number => Math.max(...pile.map((paper, r) => (pile.length - 1 - r) * step + paper.height));

  // The columns: a paper wide, a gutter apart, as many as fit (one at least). A
  // lone column wider than the desk (a phone's) keeps its left edge on the desk.
  const widest = Math.max(...stacks.map((pile) => Math.max(...pile.map((paper) => paper.width))));
  const columns = Math.max(1, Math.floor((usableWidth + options.gap) / (widest + options.gap)));
  const columnLeft = (column: number): number => {
    const left = options.origin.x + column * (widest + options.gap);
    return left + widest > surface.width ? Math.max(0, surface.width - widest) : left;
  };

  // Where each pile stands. Side by side when each has a column, the whole
  // papers on one line; otherwise each pile beneath the shortest column so far.
  const standing: StandingPile[] = [];
  if (stacks.length <= columns) {
    const whole = options.origin.y + depth * step;
    stacks.forEach((pile, column) => { standing.push({ pile, column, top: whole - (pile.length - 1) * step, height: reach(pile) }); });
  } else {
    const bottoms: number[] = Array.from({ length: columns }, () => options.origin.y);
    for (const pile of stacks) {
      const column = bottoms.indexOf(Math.min(...bottoms));
      const top = bottoms[column] ?? options.origin.y;
      const height = reach(pile);
      standing.push({ pile, column, top, height });
      bottoms[column] = top + height + options.gap;
    }
  }

  // A column that runs past the foot closes up evenly, the first pile staying
  // put and each pile below it coming no nearer than the one above's strips
  // and a strip of its whole paper — so every paper's top still shows.
  const least = Math.max(options.minStep, step);
  const tops = new Map<StandingPile, number>();
  for (let column = 0; column < columns; column += 1) {
    const inColumn = standing.filter((stood) => stood.column === column);
    const foot = Math.max(...inColumn.map((stood) => stood.top + stood.height));
    const excess = inColumn.length < 2 ? 0 : Math.max(0, foot - surface.height);
    let nearest = Number.NEGATIVE_INFINITY;
    inColumn.forEach((stood, at) => {
      const closed = stood.top - (excess * at) / (inColumn.length - 1);
      const top = at === 0 ? stood.top : Math.max(closed, nearest);
      tops.set(stood, top);
      nearest = top + (stood.pile.length - 1) * step + least;
    });
  }

  const offsets: Record<string, Point> = {};
  const order: string[] = [];
  for (let column = columns - 1; column >= 0; column -= 1) {
    for (const stood of standing.filter((candidate) => candidate.column === column)) {
      const left = columnLeft(column);
      const top = tops.get(stood) ?? stood.top;
      const pile = stood.pile;
      [...pile].reverse().forEach((paper) => {
        const r = pile.indexOf(paper);
        const y = top + (pile.length - 1 - r) * step;
        offsets[paper.key] = { x: Math.round(left - paper.rest.x), y: Math.round(y - paper.rest.y) };
        order.push(paper.key);
      });
    }
  }
  return { offsets, order };
}
